import React from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useParams,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import AuthGuard from "./components/auth/AuthGuard";
import RoleProtectedRoute from "./components/auth/RoleProtectedRoute";
import LayoutWithRouter from "./components/LayoutWithRouter";
import ErrorBoundary from "./components/shared/ErrorBoundary";

// Static Pages
import Landing from "./pages/Landing";
import About from "./pages/About";
import Application from "./pages/Application";
import Contact from "./pages/Contact";
import FAQs from "./pages/FAQs";
import RulesProcedures from "./pages/RulesProcedures";
import Process from "./pages/Process";
import Resources from "./pages/Resources";
// Authentication
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";

// Dashboards
import Dashboard from "./pages/Dashboard";

// Panels
import PanelAdminInterface from "./pages/PanelAdminInterface";
import PanelCreationEnhanced from "./pages/PanelCreationEnhanced";
import PanelDetails from "./pages/PanelDetails";
import PanelsList from "./pages/PanelsList";
import PublicPanels from "./pages/PublicPanels";

// Cadre
import CadreApplication from "./pages/CadreApplication";
import CadreProfile from "./pages/CadreProfile";

// Review
import ShortlistReview from "./pages/ShortlistReview";

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthGuard>
          <LayoutWithRouter>
            <ToastContainer
              position="top-right"
              autoClose={8000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
              role="status"
              closeButton
              ariaLabel="Notifications"
            />
            <Routes>
              {/* Public Routes - No Authentication Required */}
              <Route path="/" element={<Landing />} />
              <Route path="/about" element={<About />} />
              <Route path="/process" element={<Process />} />
              <Route path="/rules-procedures" element={<RulesProcedures />} />
              <Route path="/faqs" element={<FAQs />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/panels" element={<PublicPanels />} />
              {/* Backward compatibility for old path */}
              <Route
                path="/public-panels"
                element={<Navigate to="/panels" replace />}
              />
              <Route path="/panel/:id" element={<PanelDetails />} />
              {/* Backward compatibility for old details path */}
              <Route
                path="/panel-details/:id"
                element={<PanelDetailsRedirect />}
              />

              {/* Authentication Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/application" element={<Application />} />

              {/* Cadre Application - Public but leads to authentication */}
              <Route path="/cadre-application" element={<CadreApplication />} />

              {/* Protected Routes - Authentication Required */}
              <Route
                path="/dashboard"
                element={
                  <RoleProtectedRoute>
                    <Dashboard />
                  </RoleProtectedRoute>
                }
              />

              {/* Panel Management - Admin Only */}
              <Route
                path="/panel-creation"
                element={
                  <RoleProtectedRoute allowedRoles={["PLATFORM_ADMIN"]}>
                    <PanelCreationEnhanced />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="/panels-management"
                element={
                  <RoleProtectedRoute allowedRoles={["PLATFORM_ADMIN"]}>
                    <PanelsList />
                  </RoleProtectedRoute>
                }
              />

              {/* Panel Admin Interface - Platform Admins and Cadre Members who are panel admins */}
              <Route
                path="/panel-admin/:panelId"
                element={
                  <RoleProtectedRoute
                    allowedRoles={["PLATFORM_ADMIN", "CADRE_MEMBER"]}
                  >
                    <PanelAdminInterface />
                  </RoleProtectedRoute>
                }
              />

              {/* Cadre Profile - Cadre Members Only */}
              <Route
                path="/cadre-profile"
                element={
                  <RoleProtectedRoute allowedRoles={["CADRE_MEMBER"]}>
                    <CadreProfile />
                  </RoleProtectedRoute>
                }
              />

              {/* Shortlist Review - Committee and Client Members */}
              <Route
                path="/shortlist-review"
                element={
                  <RoleProtectedRoute
                    allowedRoles={["COMMUNITY_MEMBER", "CLIENT_MEMBER"]}
                  >
                    <ShortlistReview />
                  </RoleProtectedRoute>
                }
              />

              {/* Upload Documents - Placeholder for document uploads */}
              <Route
                path="/upload-documents"
                element={
                  <RoleProtectedRoute>
                    <div className="min-h-screen flex items-center justify-center bg-gray-50">
                      <div className="text-center">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                          Upload Documents
                        </h2>
                        <p className="text-gray-600 mb-8">
                          Document upload functionality coming soon.
                        </p>
                        <button
                          onClick={() => window.history.back()}
                          className="bg-custom-blue text-white px-6 py-3 rounded-lg hover:bg-custom-light-blue transition-colors"
                        >
                          Go Back
                        </button>
                      </div>
                    </div>
                  </RoleProtectedRoute>
                }
              />

              {/* 404 - Not Found */}
              <Route
                path="*"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <h1 className="text-6xl font-bold text-gray-300 mb-4">
                        404
                      </h1>
                      <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                        Page Not Found
                      </h2>
                      <p className="text-gray-600 mb-8">
                        The page you're looking for doesn't exist.
                      </p>
                      <a
                        href="/"
                        className="bg-custom-blue text-white px-6 py-3 rounded-lg hover:bg-custom-light-blue transition-colors"
                      >
                        Return to Home
                      </a>
                    </div>
                  </div>
                }
              />
            </Routes>
          </LayoutWithRouter>
        </AuthGuard>
      </Router>
    </ErrorBoundary>
  );
}

export default App;

// Local component to redirect old path to the new route keeping the id
function PanelDetailsRedirect() {
  const { id } = useParams();
  return <Navigate to={`/panel/${id}`} replace />;
}
